import { FC, MouseEvent, useMemo, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import {
  Button as MuiButton,
  Divider,
  Menu,
  Stack,
  Tooltip,
  styled,
} from '@mui/material';

import { TActiveCFSFilter } from '@/api';
import { IcFilter } from '@/assets/images';
import { ActiveFilterIndicator, RenderFormField } from '@/components';
import { Button } from '@/components/TailwindComponents';
import { AgencyType, Role } from '@/models';
import { isAgencyTypeRole, isDispatcherRole } from '@/services';
import { useAgencyStore, useUserStore } from '@/store';

import { filterMenuFormFields as allFields } from '../data';

interface IFilterMenuProps {
  onChangeFilter: (value: TActiveCFSFilter) => void;
}

const FormRoot = styled('form')(() => ({
  padding: 16,
  '& .divider': {
    marginBottom: 16,
  },
}));

const initialFilterValue = {
  severity: '',
  assignmentStatus: '',
  agencies: [],
  readBy: false,
  agencyType: [],
};

export const FilterMenu: FC<IFilterMenuProps> = ({ onChangeFilter }) => {
  const methods = useForm<TActiveCFSFilter>({
    defaultValues: initialFilterValue as TActiveCFSFilter,
    mode: 'all',
  });

  const { agencies } = useAgencyStore();
  const { user } = useUserStore();

  const { handleSubmit, reset } = methods;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showFilterDot, setShowFilterDot] = useState(false);

  const openActionMenu = Boolean(anchorEl);

  const handleOpenMenu = (e: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const onCancel = () => {
    handleCloseMenu();
    reset();
  };

  const onClear = () => {
    reset();
    onChangeFilter({});
    setShowFilterDot(false);
  };

  const onSubmit: SubmitHandler<TActiveCFSFilter> = (data) => {
    onChangeFilter(data);

    const isFilterDirty = Object.entries(data).some(([, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'boolean') return value === true;
      return !!value;
    });

    setShowFilterDot(isFilterDirty);
    handleCloseMenu();
  };

  const selectedAgencyTypes = methods.watch('agencyType') || [];

  const agencyOptions = useMemo(() => {
    if (!user) return [];

    let filteredAgencies = agencies;

    const isPoliceUser =
      isDispatcherRole(user) ||
      isAgencyTypeRole(user, Role.AGENCY_ADMIN, AgencyType.POLICE) ||
      isAgencyTypeRole(user, Role.FIRST_RESPONDER, AgencyType.POLICE);

    const isLimitedRole =
      isAgencyTypeRole(user, Role.AGENCY_ADMIN, AgencyType.EMS) ||
      isAgencyTypeRole(user, Role.AGENCY_ADMIN, AgencyType.FIRE) ||
      isAgencyTypeRole(user, Role.FIRST_RESPONDER, AgencyType.EMS) ||
      isAgencyTypeRole(user, Role.FIRST_RESPONDER, AgencyType.FIRE);

    // If limited role (EMS/FIRE only), restrict agencies
    if (isLimitedRole) {
      filteredAgencies = agencies.filter(
        (a) => a.type === AgencyType.EMS || a.type === AgencyType.FIRE,
      );
    }

    // For Police/Dispatcher: filter by selected types if any
    if (isPoliceUser && selectedAgencyTypes.length > 0) {
      filteredAgencies = filteredAgencies.filter((agency) =>
        selectedAgencyTypes.includes(agency.type),
      );
    }

    return filteredAgencies.map((agency) => ({
      label: agency.name,
      value: agency._id ?? '',
    }));
  }, [agencies, selectedAgencyTypes, user]);

  const filterMenuFormFields = useMemo(() => {
    if (!user) return [];

    const showAgencyType =
      isDispatcherRole(user) ||
      isAgencyTypeRole(user, Role.AGENCY_ADMIN, AgencyType.POLICE) ||
      isAgencyTypeRole(user, Role.FIRST_RESPONDER, AgencyType.POLICE);

    return allFields.filter((field) => {
      if (field.name === 'agencyType') {
        return showAgencyType;
      }
      return true;
    });
  }, [user]);

  return (
    <>
      <Tooltip title="Filters">
        <Button
          variant="outline"
          color="inherit"
          className="rounded-r-none bg-white"
          style={{ position: 'relative' }}
          onClick={handleOpenMenu}
        >
          <IcFilter />
          {showFilterDot && <ActiveFilterIndicator />}
        </Button>
      </Tooltip>

      <Menu
        id="action-menu"
        anchorEl={anchorEl}
        open={openActionMenu}
        onClose={handleCloseMenu}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <FormProvider {...methods}>
          <FormRoot onSubmit={handleSubmit(onSubmit)}>
            {filterMenuFormFields.map((field) => (
              <RenderFormField
                key={field.name}
                {...field}
                {...(field.name === 'agencies' ? { items: agencyOptions } : {})}
              />
            ))}

            <Divider className="divider" sx={{ marginTop: '1rem' }} />

            <Stack flexDirection="row" columnGap={2}>
              <Button
                type="button"
                variant="outline"
                color="inherit"
                onClick={onCancel}
              >
                Cancel
              </Button>

              <Button
                type="button"
                variant="ghost"
                color="inherit"
                onClick={onClear}
              >
                Clear
              </Button>

              <MuiButton type="submit" variant="contained" color="error">
                Apply
              </MuiButton>
            </Stack>
          </FormRoot>
        </FormProvider>
      </Menu>
    </>
  );
};
